// @ts-nocheck
import { useState, useEffect } from 'react'
import { Phone, Clock, Users, TrendingUp, BarChart3 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useApi } from '../hooks/useApi'
import { useCallStore } from '../stores/callStore'
import { usePresenceStore } from '../stores/presenceStore'
import { useTwilioDevice } from '../hooks/useTwilioDevice'
import HoldIndicator from '../components/HoldIndicator'
import ActionRequiredPanel from '../components/Dashboard/ActionRequiredPanel'
import CompactOperatorList from '../components/Dashboard/CompactOperatorList'
import { logger } from '../utils/logger'

export default function DashboardPage() {
  const navigate = useNavigate()
  
  // Zustand Selector最適化: 必要な値のみを個別に取得
  const user = useAuthStore((state) => state.user)
  const api = useApi()
  
  // グローバル通話状態を取得
  const callStatus = useCallStore((state) => state.callStatus)
  const queuedCalls = useCallStore((state) => state.queuedCalls)
  const heldCalls = useCallStore((state) => state.heldCalls)
  
  // 自分が保留した通話のみをフィルタリング
  const myHeldCalls = heldCalls.filter(call => call.operatorUserId === user?.sub)
  
  const { resumeHeldCall, makeInternalCall } = useTwilioDevice()
  
  const activeOperators = usePresenceStore((state) => state.activeOperators)
  const [callingOperatorId, setCallingOperatorId] = useState(null)
  const [stats, setStats] = useState({
    todayCalls: 0,
    averageDuration: '0:00',
    activeUsers: 0,
    responseRate: 0,
  })
  const [error, setError] = useState(null)

  // 統計データを取得
  useEffect(() => {
    if (!user?.organizationId) {
      return
    }

    const fetchStats = async () => {
      try {
        const data = await api.get('/api/stats/dashboard')
        setStats((prev) => ({
          ...prev,
          ...data,
          activeUsers: prev?.activeUsers ?? data.activeUsers ?? 0,
        }))
      } catch (err) {
        logger.error('統計取得エラー:', err)
      }
    }

    fetchStats()
    const interval = setInterval(fetchStats, 60000) // 60秒ごと

    return () => clearInterval(interval)
  }, [api, user?.organizationId])

  // presence更新に合わせてアクティブユーザー数を同期
  useEffect(() => {
    setStats((prev) => ({ ...prev, activeUsers: activeOperators.length }))
  }, [activeOperators])

  // オペレータへの内線発信
  const handleCallOperator = async (targetUserId, targetName) => {
    if (callingOperatorId) {
      return
    }

    try {
      setCallingOperatorId(targetUserId)
      setError(null)
      await makeInternalCall(targetUserId, targetName)
    } catch (err) {
      logger.error('内線発信エラー:', err)
      if (err.offline) {
        setError(`${targetName}さんはオフラインです`)
      } else {
        setError(err.message || '内線発信に失敗しました')
      }
    } finally {
      setCallingOperatorId(null)
    }
  }

  const statsDisplay = [
    {
      name: '本日の通話数',
      value: stats.todayCalls.toString(),
      icon: Phone,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    },
    {
      name: '平均通話時間',
      value: stats.averageDuration,
      icon: Clock,
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    },
    {
      name: 'アクティブユーザー',
      value: activeOperators.length.toString(),
      icon: Users,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
    },
    {
      name: '応答率',
      value: `${stats.responseRate}%`,
      icon: TrendingUp,
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    },
  ]

  return (
    <div className="relative space-y-6">
      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200 text-sm font-medium">
            {error}
          </p>
        </div>
      )}

      {/* アクション必要セクション（条件付き表示） */}
      <ActionRequiredPanel 
        queuedCallsCount={queuedCalls.length}
        heldCallsCount={myHeldCalls.length}
      />

      {/* 今日の概要 - シンプル化された統計カード */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            今日の概要
          </h2>
          <button
            onClick={() => navigate('/statistics')}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          >
            <BarChart3 className="w-4 h-4" />
            詳細な統計を見る
          </button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statsDisplay.map((stat) => (
            <div key={stat.name} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
              <div className={`w-10 h-10 ${stat.bgColor} rounded-lg flex items-center justify-center mb-3`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {stat.name}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stat.value}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* メインコンテンツ - オンラインオペレーター（全幅） */}
      <CompactOperatorList
        operators={activeOperators}
        currentUserId={user?.sub}
        onCallOperator={handleCallOperator}
        callingOperatorId={callingOperatorId}
        isCallDisabled={['connecting', 'connected'].includes(callStatus)}
      />

      {/* 左下固定: 保留インジケーター（自分が保留した通話のみ） */}
      <HoldIndicator
        calls={myHeldCalls}
        onResume={resumeHeldCall}
      />
    </div>
  )
}
